import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Button, DatePicker, Menu, Select, Input, Table } from 'antd';
import { Column } from '@ant-design/charts';
import styles from './reportStatistics.module.less';
import icon_1 from '@/asset/new/icon_1.png';
import icon_2 from '@/asset/new/icon_2.png';
import icon_3 from '@/asset/new/icon_3.png';
import icon_4 from '@/asset/new/icon_4.png';
import icon_5 from '@/asset/new/icon_5.png';
import bg_1 from '@/asset/new/bg_1.png';
import bg_2 from '@/asset/new/bg_2.png';
import bg_3 from '@/asset/new/bg_3.png';
import bg_4 from '@/asset/new/bg_4.png';
import bg_5 from '@/asset/new/bg_5.png';
import Gstyle from './index.module.less';
import dayjs from 'dayjs';
import {
    knowledgeReportSendEmailList,
    knowledgeDictionaryList,
    knowledgeApprovalList,
    knowledgeSharePage,
    knowledgeFolderListWithoutRoot,
    knowledgeLogReport,
    knowledgeLogReportShare,
} from '@/api/knowledge';
import { API_ROOT } from '@/env';
import { downloadExportFile } from './downloadExport';
const { RangePicker } = DatePicker;

const APPROVAL_STATUS_MAP = {
    pending: { label: '待审核', color: '#EA7700' },
    approved: { label: '已同意', color: '#33C249' },
    rejected: { label: '已拒绝', color: '#DE161D' },
};

function appendDateRange(params, beginKey, endKey, range) {
    if (range && range[0] && range[1]) {
        params[beginKey] = range[0].startOf('day').format('YYYY-MM-DD');
        params[endKey] = range[1].endOf('day').format('YYYY-MM-DD');
    }
}

function buildApprovalListParams({ pageNo, pageSize, folderTagId, applyTimeRange, approveTimeRange, applicantName, status }) {
    const p = { pageNo, pageSize };
    if (folderTagId != null && folderTagId !== '' && !(Array.isArray(folderTagId) && folderTagId.length === 0)) {
        p.folderTagId = Array.isArray(folderTagId) ? folderTagId : [folderTagId];
    }
    if (applicantName && String(applicantName).trim()) {
        p.applicantName = String(applicantName).trim();
    }
    if (status) {
        p.status = status;
    }
    appendDateRange(p, 'applyTimeBegin', 'applyTimeEnd', applyTimeRange);
    appendDateRange(p, 'approveTimeBegin', 'approveTimeEnd', approveTimeRange);
    return p;
}

function buildApprovalExportParams({ folderTagId, applyTimeRange, approveTimeRange, applicantName, status }) {
    const p = {};
    if (folderTagId != null && folderTagId !== '' && !(Array.isArray(folderTagId) && folderTagId.length === 0)) {
        p.folderTagId = Array.isArray(folderTagId) ? folderTagId : [folderTagId];
    }
    if (applicantName && String(applicantName).trim()) {
        p.applicantName = String(applicantName).trim();
    }
    if (status) {
        p.status = status;
    }
    appendDateRange(p, 'applyTimeBegin', 'applyTimeEnd', applyTimeRange);
    appendDateRange(p, 'approveTimeBegin', 'approveTimeEnd', approveTimeRange);
    return p;
}

function buildEmailListParams({ pageNo, pageSize, creatorName, fileName, fileApprovalCode, dateRange }) {
    const p = { pageNo, pageSize };
    if (creatorName && String(creatorName).trim()) {
        p.creatorName = String(creatorName).trim();
    }
    if (fileName && String(fileName).trim()) {
        p.fileName = String(fileName).trim();
    }
    if (fileApprovalCode && String(fileApprovalCode).trim()) {
        p.fileApprovalCode = String(fileApprovalCode).trim();
    }
    appendDateRange(p, 'createTimeBegin', 'createTimeEnd', dateRange);
    return p;
}

function buildEmailExportParams({ creatorName, fileName, fileApprovalCode, dateRange }) {
    const p = {};
    if (creatorName && String(creatorName).trim()) {
        p.creatorName = String(creatorName).trim();
    }
    if (fileName && String(fileName).trim()) {
        p.fileName = String(fileName).trim();
    }
    if (fileApprovalCode && String(fileApprovalCode).trim()) {
        p.fileApprovalCode = String(fileApprovalCode).trim();
    }
    appendDateRange(p, 'createTimeBegin', 'createTimeEnd', dateRange);
    return p;
}

function buildShareListParams({ pageNo, pageSize, folderTagId, shareTimeRange, shareUserName, operationType, id }) {
    const p = { pageNo, pageSize };
    if (folderTagId != null && folderTagId !== '' && !(Array.isArray(folderTagId) && folderTagId.length === 0)) {
        p.folderTagId = Array.isArray(folderTagId) ? folderTagId : [folderTagId];
    }
    appendDateRange(p, 'shareTimeBegin', 'shareTimeEnd', shareTimeRange);
    if (shareUserName && String(shareUserName).trim()) {
        p.shareUserName = String(shareUserName).trim();
    }
    if (operationType !== undefined && operationType !== null && operationType !== '') {
        p.operationType = operationType;
    }
    if (id && String(id).trim()) {
        p.id = String(id).trim();
    }
    return p;
}

function buildShareExportParams({ folderTagId, shareTimeRange, shareUserName, operationType, id }) {
    const p = {};
    if (folderTagId != null && folderTagId !== '' && !(Array.isArray(folderTagId) && folderTagId.length === 0)) {
        p.folderTagId = Array.isArray(folderTagId) ? folderTagId : [folderTagId];
    }
    appendDateRange(p, 'shareTimeBegin', 'shareTimeEnd', shareTimeRange);
    if (shareUserName && String(shareUserName).trim()) {
        p.shareUserName = String(shareUserName).trim();
    }
    if (operationType !== undefined && operationType !== null && operationType !== '') {
        p.operationType = operationType;
    }
    if (id && String(id).trim()) {
        p.id = String(id).trim();
    }
    return p;
}

/** 按列配置的 width 固定列宽（避免被内容/容器拉伸） */
function applyColumnWidths(cols) {
    return (cols || []).map(col => {
        const w = Number(col.width);
        if (!Number.isFinite(w) || w <= 0) {
            return col;
        }
        return {
            ...col,
            width: w,
            onHeaderCell: () => ({ style: { width: w, minWidth: w, maxWidth: w } }),
            onCell: () => ({ style: { width: w, minWidth: w, maxWidth: w } }),
        };
    });
}

/** 横向滚动宽度 = 各列 width 之和（如文件名称 240 会计入） */
function getTableScrollX(cols) {
    const sum = (cols || []).reduce((s, col) => s + (Number(col.width) || 0), 0);
    return sum > 0 ? sum : true;
}

function isApiSuccess(res) {
    return res && (res.status === 200 || res.success === true);
}

function buildReportTimeParams(range) {
    const p = { createTimeStart: '', createTimeEnd: '' };
    if (range && range[0] && range[1]) {
        p.createTimeStart = range[0].startOf('day').format('YYYY-MM-DD');
        p.createTimeEnd = range[1].endOf('day').format('YYYY-MM-DD');
    }
    return p;
}

const CARD_META = [
    { key: 'totalUserCount', title: '总用户数', bg_img: bg_1, icon: icon_1, bg: '#EDF5FF' },
    { key: 'shareCount', title: '分享次数', bg_img: bg_2, icon: icon_2, bg: '#FFF4E8' },
    { key: 'viewCount', title: '医生浏览次数', bg_img: bg_3, icon: icon_3, bg: '#FFF0F0' },
    { key: 'downloadCount', title: '医生下载次数', bg_img: bg_4, icon: icon_4, bg: '#E9FCFE' },
    { key: 'emailSendCount', title: '邮箱发送次数', bg_img: bg_5, icon: icon_5, bg: '#FFF4E8' },
];

const EMPTY_REPORT_STATS = {
    totalUserCount: 0,
    shareCount: 0,
    viewCount: 0,
    downloadCount: 0,
    emailSendCount: 0,
};

const ReportStatistics = () => {
    const [filterForm] = Form.useForm();
    const [reportForm] = Form.useForm();
    const [tabkey, setTabkey] = useState('shareRecord');
    const [dataSource, setDataSource] = useState([]);
    const [columns, setColumns] = useState([]);
    const [total, setTotal] = useState(0);
    const [current, setCurrent] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [emailLoading, setEmailLoading] = useState(false);
    const [approvalLoading, setApprovalLoading] = useState(false);
    const [shareLoading, setShareLoading] = useState(false);
    const [appliedEmailFilters, setAppliedEmailFilters] = useState({
        creatorName: '',
        fileName: '',
        fileApprovalCode: '',
        dateRange: null,
    });
    const [appliedApprovalFilters, setAppliedApprovalFilters] = useState({
        folderTagId: undefined,
        applyTimeRange: null,
        approveTimeRange: null,
        applicantName: '',
        status: undefined,
    });
    const [appliedShareFilters, setAppliedShareFilters] = useState({
        folderTagId: undefined,
        shareTimeRange: null,
        shareUserName: '',
        operationType: undefined,
        id: '',
    });
    /** 字典下拉：BU / ta（适应症）；TA 筛选项来自 folder/list/without-root */
    const [dictBuOptions, setDictBuOptions] = useState([]);
    const [dictTaOptions, setDictTaOptions] = useState([]);
    const [folderTagOptions, setFolderTagOptions] = useState([]);
    const [reportStats, setReportStats] = useState(EMPTY_REPORT_STATS);
    const [shareChartData, setShareChartData] = useState([]);
    const [reportLoading, setReportLoading] = useState(false);

    const cardList = useMemo(
        () =>
            CARD_META.map(item => ({
                ...item,
                num: reportStats[item.key] ?? 0,
            })),
        [reportStats],
    );

    const shareChartConfig = useMemo(
        () => ({
            data: shareChartData,
            xField: 'folderName',
            yField: 'shareCount',
            color: '#071D49',
            height: 400,
            appendPadding: [20, 0, 0, 0],
            label: {
                position: 'top',
                style: {
                    fill: '#333333',
                    fontSize: 12,
                    fontWeight: 600,
                },
            },
            meta: {
                folderName: {
                    alias: '类别',
                },
                shareCount: {
                    alias: '数量',
                },
            },
            minColumnWidth: 24,
            maxColumnWidth: 24,
        }),
        [shareChartData],
    );
    // 表格列配置
    const shareRecord = [
        {
            title: 'BU',
            dataIndex: 'bu',
            key: 'bu',
            width: 100,
            render: (_, record) =>
                formatDictValueAsLabel(record.klsFileVo?.bu, dictBuOptions),
        },
        {
            title: 'TA',
            dataIndex: 'taTagNames',
            key: 'taTagNames',
            width: 120,
            ellipsis: true,
            render: (_, record) => {
                const names = record.klsFileVo?.folderTagName;
                return names;
            },
        },
        {
            title: '文件名称',
            dataIndex: 'fileName',
            key: 'fileName',
            width: 240,
            ellipsis: true,
            render: (_, record) => record.klsFileVo?.name || ' ',
        },
        {
            title: '文件审批号',
            dataIndex: 'approvalCode',
            key: 'approvalCode',
            width: 160,
            ellipsis: true,
            render: (_, record) => record.klsFileVo?.approvalCode || ' ',
        },
        { title: '分享人', dataIndex: 'creatorName', key: 'creatorName', width: 140 },
        { title: '分享人UPI', dataIndex: 'shareUserUpi', key: 'shareUserUpi', width: 140 },
        {
            title: '分享时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 150,
            render: text => {
                if (!text) return ' ';
                return dayjs(text).format('YYYY-MM-DD HH:mm');
            },
        },
        {
            title: '分享外部对象',
            dataIndex: 'shareTarget',
            key: 'shareTarget',
            width: 140,
            ellipsis: true,
            render: (_, record) => {
                const hcp = record.shareHcpInfo;
                if (!hcp) {
                    return ' ';
                }
                if (hcp.hcpType === 'A') {
                    const name = hcp.name || '';
                    const realName = hcp.realName || '';
                    if (name && realName) {
                        return `${name}(${realName})`;
                    }
                    return name || realName || ' ';
                }
                return hcp.name || ' ';
            },
        },
        {
            title: '外部对象ID',
            dataIndex: 'externalUserid',
            key: 'externalUserid',
            width: 160,
            ellipsis: true,
            render: (_, record) =>
                record.shareHcpInfo?.externalUserid || record.shareHcpInfo?.hcpId || ' ',
        },
        {
            title: '医生VID',
            dataIndex: 'vid',
            key: 'vid',
            width: 120,
            ellipsis: true,
            render: (_, record) =>
                record.shareHcpInfo?.vid || ' ',
        },
        {
            title: '科室名称',
            dataIndex: 'departName',
            key: 'departName',
            width: 100,
            render: (_, record) => record.shareHcpInfo?.departName || ' ',
        },
        {
            title: '医院名称',
            dataIndex: 'hospitalName',
            key: 'hospitalName',
            width: 120,
            ellipsis: true,
            render: (_, record) => record.shareHcpInfo?.hospitalName || ' ',
        },
        {
            title: '预览',
            dataIndex: 'isPreview',
            key: 'isPreview',
            width: 80,
            render: val => (val ? '是' : '否'),
        },
        {
            title: '下载',
            dataIndex: 'isDownload',
            key: 'isDownload',
            width: 80,
            render: (val, record) => {
                if (record.shareHcpInfo?.hcpType !== 'A') {
                    return 'N/A';
                }
                return val ? '是' : '否';
            },
        },
        {
            title: '外部最后操作时间',
            dataIndex: 'lastOpTime',
            key: 'lastOpTime',
            width: 150,
            render: text => {
                if (!text) return ' ';
                return dayjs(text).format('YYYY-MM-DD HH:mm');
            },
        },
        { title: '记录ID', dataIndex: 'id', key: 'id', width: 180, ellipsis: true },
    ];
    const approvalStatus = [
        {
            title: 'BU',
            dataIndex: 'bu',
            key: 'bu',
            width: 100,
            render: text => formatDictValueAsLabel(text, dictBuOptions),
        },
        {
            title: 'TA',
            dataIndex: 'ta',
            key: 'ta',
            width: 100,
        },
        { title: '文件名称', dataIndex: 'name', key: 'name', width: 240, ellipsis: true },
        { title: '文件审批号', dataIndex: 'approvalCode', key: 'approvalCode', width: 100 },
        { title: '申请人', dataIndex: 'applicantName', key: 'applicantName', width: 100 },
        { title: '申请人UPI', dataIndex: 'applicantUpi', key: 'applicantUpi', width: 120 },
        {
            title: '申请时间',
            dataIndex: 'applyTime',
            key: 'applyTime',
            width: 100,
            render: text => {
                if (!text) return ' ';
                return dayjs(text).format('YYYY-MM-DD HH:mm');
            },
        },
        { title: '审批人', dataIndex: 'approverName', key: 'approverName', width: 120 },
        { title: '审批人UPI', dataIndex: 'approverUpi', key: 'approverUpi', width: 100 },
        {
            title: '审批状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: status => {
                if (status == null || status === '') {
                    return ' ';
                }
                const item = APPROVAL_STATUS_MAP[status];
                if (!item) {
                    return String(status);
                }
                return <span style={{ color: item.color }}>{item.label}</span>;
            },
        },
        {
            title: '审批时间',
            dataIndex: 'approveTime',
            key: 'approveTime',
            width: 120,
            render: text => {
                if (!text) return ' ';
                return dayjs(text).format('YYYY-MM-DD HH:mm');
            },
        },
        { title: '审批意见', dataIndex: 'approveComment', key: 'approveComment', width: 100, ellipsis: true },
    ];
    const emailStatus = [
        { title: 'BU', dataIndex: 'bu', key: 'bu', width: 100, render: text => formatDictValueAsLabel(text, dictBuOptions) },
        { title: 'TA', dataIndex: 'ta', key: 'ta', width: 100 },
        { title: '文件名称', dataIndex: 'name', key: 'name', width: 240, ellipsis: true },
        { title: '文件审批号', dataIndex: 'approvalCode', key: 'approvalCode', width: 100 },
        { title: '申请人', dataIndex: 'creatorName', key: 'creatorName', width: 100 },
        { title: '申请人UPI', dataIndex: 'upi', key: 'upi', width: 100 },
        { title: '申请记录ID', dataIndex: 'approvalId', key: 'approvalId', width: 100 },
        {
            title: '审批时间',
            dataIndex: 'approvalTime',
            key: 'approvalTime',
            width: 100,
            render: text => {
                if (!text) return ' ';
                return dayjs(text).format('YYYY-MM-DD HH:mm');
            },
        },
        {
            title: '发件时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 100,
            render: text => {
                if (!text) return ' ';
                return dayjs(text).format('YYYY-MM-DD HH:mm');
            },
        },
        { title: '员工邮箱', dataIndex: 'email', key: 'email', width: 120 },
        {
            title: '邮件发送结果',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: status => {
                if (status === '' || status === undefined || status === null) {
                    return ' ';
                }
                const n = Number(status);
                const map = {
                    0: { label: '待发送', color: '#EA7700' },
                    1: { label: '发送成功', color: '#33C249' },
                    [-1]: { label: '发送失败', color: '#DE161D' },
                };
                const item = map[n];
                if (!item) {
                    return String(status);
                }
                return <span style={{ color: item.color }}>{item.label}</span>;
            },
        },
        { title: '发送失败原因', dataIndex: 'failureReason', key: 'failureReason', width: 100 },
    ];
    /** 字典列表 → Select options（展示 dictLabel，提交 dictValue） */
    function mapDictListToOptions(list) {
        if (!Array.isArray(list)) {
            return [];
        }
        return list.map(item => ({
            label: item.dictLabel,
            value: item.dictValue,
        }));
    }

    /** 表格展示：接口返回的 dictValue 映射为 dictLabel（与下拉 options 一致） */
    function formatDictValueAsLabel(value, options) {
        if (value == null || value === '') {
            return ' ';
        }
        const list = Array.isArray(options) ? options : [];
        const hit = list.find(o => String(o.value) === String(value));
        return hit != null ? hit.label : String(value);
    }

    function normalizeDictRows(res) {
        if (!res || res.status !== 200 || res.data == null) {
            return [];
        }
        const raw = res.data;
        if (Array.isArray(raw)) {
            return raw;
        }
        return raw.records ?? raw.list ?? raw.rows ?? [];
    }

    function mapFolderListToOptions(list) {
        if (!Array.isArray(list)) {
            return [];
        }
        return list.map(item => ({
            label: item.name || String(item.id),
            value: item.id,
        }));
    }

    async function fetchFolderTagOptions() {
        const res = await knowledgeFolderListWithoutRoot();
        if (!isApiSuccess(res) || res.data == null) {
            return [];
        }
        const list = Array.isArray(res.data) ? res.data : [];
        return mapFolderListToOptions(list);
    }
    const onsetTabkey = key => {
        setTabkey(key);
        setCurrent(1);
    };

    const fetchReportData = useCallback(async range => {
        const params = buildReportTimeParams(range);
        setReportLoading(true);
        try {
            const [reportRes, shareRes] = await Promise.all([
                knowledgeLogReport(params),
                knowledgeLogReportShare(params),
            ]);
            if (isApiSuccess(reportRes) && reportRes.data != null) {
                const d = reportRes.data;
                setReportStats({
                    totalUserCount: d.totalUserCount ?? 0,
                    shareCount: d.shareCount ?? 0,
                    viewCount: d.viewCount ?? 0,
                    downloadCount: d.downloadCount ?? 0,
                    emailSendCount: d.emailSendCount ?? 0,
                });
            } else {
                setReportStats(EMPTY_REPORT_STATS);
            }
            if (isApiSuccess(shareRes) && Array.isArray(shareRes.data)) {
                setShareChartData(
                    shareRes.data.map(item => ({
                        folderName: item.folderName || ' ',
                        shareCount: item.shareCount ?? 0,
                    })),
                );
            } else {
                setShareChartData([]);
            }
        } catch {
            setReportStats(EMPTY_REPORT_STATS);
            setShareChartData([]);
        } finally {
            setReportLoading(false);
        }
    }, []);

    const onReportSearch = () => {
        const range = reportForm.getFieldValue('reportTimeRange');
        fetchReportData(range);
    };

    const onReportReset = () => {
        reportForm.resetFields(['reportTimeRange']);
        fetchReportData(null);
    };

    const onReportLastDays = days => {
        const range = [
            dayjs()
                .subtract(days - 1, 'day')
                .startOf('day'),
            dayjs().endOf('day'),
        ];
        reportForm.setFieldsValue({ reportTimeRange: range });
        fetchReportData(range);
    };

    const fetchEmailList = useCallback(async () => {
        if (tabkey !== 'emailStatus') {
            return;
        }
        setEmailLoading(true);
        try {
            const params = buildEmailListParams({
                pageNo: current,
                pageSize,
                ...appliedEmailFilters,
            });
            const res = await knowledgeReportSendEmailList(params);
            if (res.status === 200 && res.data != null) {
                const records = res.data.records ?? res.data.list ?? res.data.rows ?? [];
                setDataSource(Array.isArray(records) ? records : []);
                setTotal(res.data.total != null ? res.data.total : 0);
            } else {
                setDataSource([]);
                setTotal(0);
            }
        } catch {
            setDataSource([]);
            setTotal(0);
        } finally {
            setEmailLoading(false);
        }
    }, [tabkey, current, pageSize, appliedEmailFilters]);

    const onEmailSearch = () => {
        const values = filterForm.getFieldsValue();
        setAppliedEmailFilters({
            creatorName: values.creatorName,
            fileName: values.fileName,
            fileApprovalCode: values.fileApprovalCode,
            dateRange: values.dateRange,
        });
        setCurrent(1);
    };

    const onEmailReset = () => {
        filterForm.resetFields([
            'creatorName',
            'fileName',
            'fileApprovalCode',
            'dateRange',
        ]);
        setAppliedEmailFilters({
            creatorName: '',
            fileName: '',
            fileApprovalCode: '',
            dateRange: null,
        });
        setCurrent(1);
    };

    const onEmailExport = () => {
        downloadExportFile({
            url: `${API_ROOT}/knowledge/apply/sendEmail/export`,
            method: 'post',
            params: buildEmailExportParams(appliedEmailFilters),
            fileName: `邮箱发送状态_${dayjs().format('YYYYMMDD')}.xlsx`,
        });
    };

    const fetchShareList = useCallback(async () => {
        if (tabkey !== 'shareRecord') {
            return;
        }
        setShareLoading(true);
        try {
            const params = buildShareListParams({
                pageNo: current,
                pageSize,
                ...appliedShareFilters,
            });
            const res = await knowledgeSharePage(params);
            if ((res.status === 200 || res.success === true) && res.data != null) {
                const records = res.data.records ?? res.data.list ?? res.data.rows ?? [];
                setDataSource(Array.isArray(records) ? records : []);
                setTotal(res.data.total != null ? res.data.total : 0);
            } else {
                setDataSource([]);
                setTotal(0);
            }
        } catch {
            setDataSource([]);
            setTotal(0);
        } finally {
            setShareLoading(false);
        }
    }, [tabkey, current, pageSize, appliedShareFilters]);

    const onShareSearch = () => {
        const values = filterForm.getFieldsValue();
        setAppliedShareFilters({
            folderTagId: values.folderTagId,
            shareTimeRange: values.shareTimeRange,
            shareUserName: values.shareUserName,
            operationType: values.operationType,
            id: values.id,
        });
        setCurrent(1);
    };

    const onShareReset = () => {
        filterForm.resetFields(['folderTagId', 'shareTimeRange', 'shareUserName', 'operationType', 'id']);
        setAppliedShareFilters({
            folderTagId: undefined,
            shareTimeRange: null,
            shareUserName: '',
            operationType: undefined,
            id: '',
        });
        setCurrent(1);
    };

    const onShareExport = () => {
        downloadExportFile({
            url: `${API_ROOT}/knowledge/share/export`,
            method: 'post',
            params: buildShareExportParams(appliedShareFilters),
            fileName: `分享记录_${dayjs().format('YYYYMMDD')}.xlsx`,
        });
    };

    const fetchApprovalList = useCallback(async () => {
        if (tabkey !== 'approvalStatus') {
            return;
        }
        setApprovalLoading(true);
        try {
            const params = buildApprovalListParams({
                pageNo: current,
                pageSize,
                ...appliedApprovalFilters,
            });
            const res = await knowledgeApprovalList(params);
            if (res.status === 200 && res.data != null) {
                const records = res.data.records ?? res.data.list ?? res.data.rows ?? [];
                setDataSource(Array.isArray(records) ? records : []);
                setTotal(res.data.total != null ? res.data.total : 0);
            } else {
                setDataSource([]);
                setTotal(0);
            }
        } catch {
            setDataSource([]);
            setTotal(0);
        } finally {
            setApprovalLoading(false);
        }
    }, [tabkey, current, pageSize, appliedApprovalFilters]);

    const onApprovalSearch = () => {
        const values = filterForm.getFieldsValue();
        setAppliedApprovalFilters({
            folderTagId: values.folderTagId,
            applyTimeRange: values.applyTimeRange,
            approveTimeRange: values.approveTimeRange,
            applicantName: values.applicantName,
            status: values.status,
        });
        setCurrent(1);
    };

    const onApprovalReset = () => {
        filterForm.resetFields(['folderTagId', 'applyTimeRange', 'approveTimeRange', 'applicantName', 'status']);
        setAppliedApprovalFilters({
            folderTagId: undefined,
            applyTimeRange: null,
            approveTimeRange: null,
            applicantName: '',
            status: undefined,
        });
        setCurrent(1);
    };

    const onApprovalExport = () => {
        downloadExportFile({
            url: `${API_ROOT}/knowledge/approval/export`,
            method: 'post',
            params: buildApprovalExportParams(appliedApprovalFilters),
            fileName: `审批状态_${dayjs().format('YYYYMMDD')}.xlsx`,
        });
    };

    const onTableSearch = () => {
        if (tabkey === 'shareRecord') {
            onShareSearch();
        } else if (tabkey === 'emailStatus') {
            onEmailSearch();
        } else if (tabkey === 'approvalStatus') {
            onApprovalSearch();
        }
    };

    const onTableReset = () => {
        if (tabkey === 'shareRecord') {
            onShareReset();
        } else if (tabkey === 'emailStatus') {
            onEmailReset();
        } else if (tabkey === 'approvalStatus') {
            onApprovalReset();
        }
    };

    const onTableExport = () => {
        if (tabkey === 'shareRecord') {
            onShareExport();
        } else if (tabkey === 'approvalStatus') {
            onApprovalExport();
        } else if (tabkey === 'emailStatus') {
            onEmailExport();
        }
    };

    useEffect(() => {
        let List = [];
        switch (tabkey) {
            case 'shareRecord':
                List = shareRecord;
                break;
            case 'approvalStatus':
                List = approvalStatus;
                break;
            case 'emailStatus':
                List = emailStatus;
                break;
            default:
        }
        setColumns(applyColumnWidths(List));
    }, [tabkey, dictBuOptions, dictTaOptions, folderTagOptions]);

    useEffect(() => {
        if (tabkey === 'shareRecord') {
            fetchShareList();
        }
    }, [fetchShareList, tabkey]);

    useEffect(() => {
        if (tabkey === 'emailStatus') {
            fetchEmailList();
        }
    }, [fetchEmailList, tabkey]);

    useEffect(() => {
        if (tabkey === 'approvalStatus') {
            fetchApprovalList();
        }
    }, [fetchApprovalList, tabkey]);
    useEffect(() => {
        fetchReportData(null);
    }, [fetchReportData]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [buRes, taRes] = await Promise.all([
                    knowledgeDictionaryList({ dictType: 'BU' }),
                    knowledgeDictionaryList({ dictType: 'ta' }),
                ]);
                if (cancelled) {
                    return;
                }
                setDictBuOptions(mapDictListToOptions(normalizeDictRows(buRes)));
                setDictTaOptions(mapDictListToOptions(normalizeDictRows(taRes)));
                const folderOptions = await fetchFolderTagOptions();
                if (cancelled) {
                    return;
                }
                setFolderTagOptions(folderOptions);
            } catch {
                if (!cancelled) {
                    setDictBuOptions([]);
                    setDictTaOptions([]);
                    setFolderTagOptions([]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);
    return (
        <>
            <style>
                {`
	                .ant-menu-submenu-arrow {
	                    color: #071D49 !important;
	                }
	            `}
            </style>
            <div className={styles.Layout}>
                <div className={styles.Content}>
                    <div className={styles.Header}>
                        <div className={styles.Title}>报告统计</div>
                        <Form
                            form={reportForm}
                            layout="inline"
                            className={styles.filterForm}
                            style={{ justifyContent: 'flex-end' }}
                        >
                            <Form.Item label="时间范围" name="reportTimeRange">
                                <RangePicker placeholder={['年/月/日', '年/月/日']} />
                            </Form.Item>
                            <Form.Item>
                                <Button type="primary" loading={reportLoading} onClick={onReportSearch}>
                                    查询
                                </Button>
                                <Button
                                    type="primary"
                                    style={{ marginLeft: 30 }}
                                    disabled={reportLoading}
                                    onClick={onReportReset}
                                >
                                    重置
                                </Button>
                                <Button
                                    type="primary"
                                    style={{ marginLeft: 30 }}
                                    disabled={reportLoading}
                                    onClick={() => onReportLastDays(7)}
                                >
                                    过去7天
                                </Button>
                                <Button
                                    type="primary"
                                    style={{ marginLeft: 30 }}
                                    disabled={reportLoading}
                                    onClick={() => onReportLastDays(30)}
                                >
                                    过去30天
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                    <div className={styles.Card}>
                        {cardList.map(item => (
                            <div
                                style={{
                                    backgroundImage: `url(${item.bg_img})`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundColor: item.bg,
                                }}
                                key={item.title}
                                className={styles.CardItem}
                            >
                                <img src={item.icon} alt={item.title} />
                                <div>
                                    <div className={styles.CardTitle}>{item.title}</div>
                                    <div className={styles.CardNum}>{item.num}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={styles.Chart}>
                        <div className={styles.sectionTitle}>分享次数</div>
                        <Column {...shareChartConfig} />
                    </div>
                </div>
                <div className={styles.Content}>
                    <Menu
                        mode="horizontal"
                        className={styles.tableMenu}
                        triggerSubMenuAction="click"
                        selectedKeys={[tabkey]}
                        onClick={e => onsetTabkey(e.key)}
                    >
                        <Menu.Item
                            key="shareRecord"
                            className={`${styles.meunitem} ${tabkey !== 'shareRecord' ? styles.meunactiv : ''
                                }`}
                        >
                            分享记录
                        </Menu.Item>
                        <Menu.Item
                            key="approvalStatus"
                            className={`${styles.meunitem} ${tabkey !== 'approvalStatus' ? styles.meunactiv : ''
                                }`}
                        >
                            审批状态
                        </Menu.Item>
                        <Menu.Item
                            key="emailStatus"
                            className={`${styles.meunitem} ${tabkey !== 'emailStatus' ? styles.meunactiv : ''
                                }`}
                        >
                            邮箱发送状态
                        </Menu.Item>
                    </Menu>
                    <Form form={filterForm} layout="inline" className={styles.filterForm}>
                        {tabkey === 'shareRecord' && (
                            <>
                                <Form.Item label="TA文件夹名称" name="folderTagId">
                                    <Select
                                        mode="multiple"
                                        placeholder="请选择TA"
                                        options={folderTagOptions}
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        maxTagCount="responsive"
                                        style={{ minWidth: 170 }}
                                    />
                                </Form.Item>
                                <Form.Item label="分享日期" name="shareTimeRange">
                                    <RangePicker placeholder={['年/月/日', '年/月/日']} style={{ width: 300 }} />
                                </Form.Item>
                                <Form.Item label="分享人" name="shareUserName">
                                    <Input placeholder="请输入" allowClear style={{ width: 170 }} />
                                </Form.Item>
                                <Form.Item label="外部操作类型" name="operationType">
                                    <Select
                                        placeholder="请选择"
                                        options={[
                                            { value: 0, label: '预览' },
                                            { value: 1, label: '下载' },
                                        ]}
                                        allowClear
                                        style={{ width: 170 }}
                                    />
                                </Form.Item>
                                <Form.Item label="记录ID" name="id">
                                    <Input placeholder="请输入" allowClear style={{ width: 170 }} />
                                </Form.Item>
                            </>
                        )}
                        {tabkey === 'approvalStatus' && (
                            <>
                                <Form.Item label="TA文件夹名称" name="folderTagId">
                                    <Select
                                        mode="multiple"
                                        placeholder="请选择TA"
                                        options={folderTagOptions}
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        maxTagCount="responsive"
                                        style={{ minWidth: 160 }}
                                    />
                                </Form.Item>
                                <Form.Item label="申请时间" name="applyTimeRange">
                                    <RangePicker placeholder={['年/月/日', '年/月/日']} style={{ width: 280 }} />
                                </Form.Item>
                                <Form.Item label="审批时间" name="approveTimeRange">
                                    <RangePicker placeholder={['年/月/日', '年/月/日']} style={{ width: 280 }} />
                                </Form.Item>
                                <Form.Item label="申请人" name="applicantName">
                                    <Input placeholder="请输入" allowClear style={{ width: 140 }} />
                                </Form.Item>
                                <Form.Item label="审批状态" name="status">
                                    <Select
                                        placeholder="请选择"
                                        options={[
                                            { value: 'pending', label: '待审核' },
                                            { value: 'approved', label: '已同意' },
                                            { value: 'rejected', label: '已拒绝' },
                                        ]}
                                        allowClear
                                        style={{ width: 140 }}
                                    />
                                </Form.Item>
                            </>
                        )}
                        {tabkey === 'emailStatus' && (
                            <>
                                <Form.Item label="发件人" name="creatorName">
                                    <Input placeholder="请输入" allowClear style={{ width: 170 }} />
                                </Form.Item>
                                <Form.Item label="文件名称" name="fileName">
                                    <Input placeholder="请输入" allowClear style={{ width: 170 }} />
                                </Form.Item>
                                <Form.Item label="文件审批号" name="fileApprovalCode">
                                    <Input placeholder="请输入" allowClear style={{ width: 170 }} />
                                </Form.Item>
                                <Form.Item label="分享日期" name="dateRange">
                                    <RangePicker placeholder={['年/月/日', '年/月/日']} />
                                </Form.Item>
                            </>
                        )}

                        <div className={styles.contentHeaderButton}>
                            <Form.Item>
                                <Button type="primary" onClick={onTableSearch}>查询</Button>
                                <Button type="primary" style={{ marginLeft: 30 }} onClick={onTableReset}>
                                    重置
                                </Button>
                                <Button type="primary" style={{ marginLeft: 30 }} onClick={onTableExport}>
                                    导出数据
                                </Button>
                            </Form.Item>
                        </div>
                    </Form>
                    <Table
                        className={Gstyle.table}
                        columns={columns}
                        dataSource={dataSource}
                        scroll={{ x: getTableScrollX(columns) }}
                        loading={
                            (tabkey === 'shareRecord' && shareLoading) ||
                            (tabkey === 'emailStatus' && emailLoading) ||
                            (tabkey === 'approvalStatus' && approvalLoading)
                        }
                        rowKey={(record, index) => record.id ?? index}
                        pagination={{
                            total: total,
                            pageSize: pageSize,
                            current: current,
                            showTotal: total => `共 ${total} 条`,
                            showQuickJumper: true,
                            showSizeChanger: true,
                            onChange: (page, size) => {
                                setCurrent(page);
                                setPageSize(size);
                            },
                        }}
                        rowClassName={(record, index) => {
                            const isMultipleOfTwo = (index + 1) % 2 === 0;
                            return isMultipleOfTwo ? Gstyle.tableRow : '';
                        }}
                    />
                </div>
            </div>
        </>
    );
};
export default ReportStatistics;
